import { useQuery } from '@tanstack/react-query';
import {
  DEFAULT_CATEGORY_LABELS,
  SUB_CATEGORIES,
  type CourseCategory,
} from '@admitly/shared';
import { apiGet } from '../lib/api.js';
import type { CategoriesResponse } from '../types/api.js';

export function useCategories() {
  return useQuery({
    queryKey: ['tenant', 'categories'],
    queryFn: () => apiGet<CategoriesResponse>('/tenant/categories'),
    staleTime: 5 * 60_000,
  });
}

/**
 * Shared label resolver: returns the tenant's custom category / sub-category
 * label (from the merged /tenant/categories response), falling back to the
 * system default. Use this everywhere a label renders so it stays consistent.
 */
export function useCategoryLabelResolver() {
  const { data } = useCategories();

  function categoryLabel(category: CourseCategory): string {
    return (
      data?.categories.find((c) => c.category === category)?.label ??
      DEFAULT_CATEGORY_LABELS[category]
    );
  }

  function subCategoryLabel(category: CourseCategory, slug: string): string {
    const fromApi = data?.categories
      .find((c) => c.category === category)
      ?.subCategories.find((s) => s.slug === slug)?.label;
    if (fromApi) return fromApi;
    return SUB_CATEGORIES[category].find((s) => s.slug === slug)?.label ?? slug;
  }

  return { categoryLabel, subCategoryLabel };
}
